import { useEffect, useMemo, useState } from 'react';
import { connect } from 'redux-bundler-react';
import { ModalContent, ModalFooter } from '@components/modal';
import Icon from '@components/icon/Icon';
import Loader from '@components/loader/Loader';
import { mdiDownloadMultiple, mdiAlertOutline } from '@mdi/js';
import FileRowActions from './FileRowActions';
import { StorageScopes, FileSubFolder, FileSection } from '@src/utils/enums';
import '@styles/_buttons.scss';
import Table from '@src/app-components/table';
import dayjs from '@src/utils/dayjs';

const buildZipFilePath = (item) => {
  if (item.section === FileSection.AdditionalInformationRequest && item.ormAdditionalInfoActionID) {
    return `${FileSubFolder.AdditionalInformationResponses}/${item.ormAdditionalInfoActionID}/${item.fileName}`;
  }

  if (item.DocumentType === FileSection.Generated) {
    return `${FileSubFolder.Generated}/${item.fileName}`;
  }

  return item.fileName;
};

const handleDownloadAllHelper = (requestUploadedFiles, projectId, requestId, version, doDownloadZip) => {
  const filePaths = requestUploadedFiles?.map(buildZipFilePath);

  doDownloadZip(filePaths, {
    projectID: projectId,
    requestID: requestId,
    version: version ?? 0,
  });
};

const headerData = [
  [
    {
      label: 'Actions',
      name: 'actions',
      isSortable: false,
      alignment: 'center',
      width: '4rem',
    },
    {
      label: 'File Name',
      name: 'fileName',
    },
    {
      label: 'Section',
      name: 'section',
    },
    {
      label: 'Last Modified',
      name: 'lastModified',
    },
  ],
];

const columnOrder = [
  { name: 'actions', alignment: 'center' },
  { name: 'fileName' },
  { name: 'section' },
  { name: 'lastModified' },
];

const FilesModal = connect(
  'doDownloadZip',
  'doFetchUserFilesDetails',
  'doResetUsersFileData',
  'selectRequestUploadedFiles',
  ({
    doDownloadZip,
    doFetchUserFilesDetails,
    doResetUsersFileData,
    requestUploadedFiles,
    id,
    projectId,
    requestId,
    version,
  }) => {
    const [isDataLoading, setIsDataLoading] = useState(true);

    const rowData = useMemo(() => {
      if (!requestUploadedFiles?.length) return [];

      return requestUploadedFiles?.map((file) => {
        const row = {
          id: file.id,
          actions: (
            <FileRowActions
              key={`actions-${file.id}`}
              fileData={file}
              projectId={projectId}
              requestId={requestId}
              version={version}
            />
          ),
          fileName: file.fileName,
          lastModified: dayjs.utc(file.lastModified).format('YYYY-MM-DD'),
          section: file.section,
        };

        return row;
      });
    }, [projectId, requestId, requestUploadedFiles, version]);

    const handleDownloadAll = () =>
      handleDownloadAllHelper(requestUploadedFiles, projectId, requestId, version, doDownloadZip);

    useEffect(() => {
      const paramsID = { projectID: projectId, requestID: requestId, version: 0 };
      // Fetch all request files from S3
      setIsDataLoading(true);
      doResetUsersFileData();
      const myPromise = new Promise((resolve) => {
        resolve(
          doFetchUserFilesDetails({
            componentID: id,
            getAllVersions: false,
            paramsID: paramsID,
            scope: StorageScopes.ProjectAndAllSubFolders,
          })
        );
      });
      Promise.all([myPromise]).then((res) => {
        res && setIsDataLoading(false);
      });
    }, [id, doFetchUserFilesDetails, doResetUsersFileData, projectId, requestId]);

    return (
      <ModalContent title='Documents'>
        <div className='modal-body'>
          <div className='padding-bottom-4'>
            {isDataLoading && <Loader variant='modal' />}
            {!isDataLoading && requestUploadedFiles?.length === 0 && (
              <>
                <Icon focusable={false} path={mdiAlertOutline} size='16px' />A generated PDF of your request is not
                available.
              </>
            )}
            {!isDataLoading && (
              <Table columnOrder={columnOrder} headerData={headerData} rowData={rowData} showPageSizeSelector={false} />
            )}
          </div>
          <ModalFooter
            onSave={handleDownloadAll}
            saveText={
              <>
                <Icon focusable={false} path={mdiDownloadMultiple} size={'16px'} /> Download All
              </>
            }
            showCancelButton
            showSaveButton={requestUploadedFiles?.length !== 0 && !isDataLoading}
          />
        </div>
      </ModalContent>
    );
  }
);

export default FilesModal;
